import React from 'react';
import { Button } from '@deriv-com/quill-ui';
import { localize } from '@deriv-com/translations';
import DFundedCore from '../../_common/base/dfunded/core';

/**
 * Sign in with a DFunded Account ID and the trading password from the
 * account email. On success the page reloads: the socket layer then opens
 * the DFunded connection (socket_base.js) and the first balance response
 * signs the trader in, the same path a Deriv login takes.
 */
const DFundedLoginModal = ({ isOpen, onClose }) => {
    const [loginId, setLoginId] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [error, setError] = React.useState('');
    const [busy, setBusy] = React.useState(false);

    React.useEffect(() => {
        if (!isOpen) return undefined;
        const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isOpen, busy, onClose]);

    if (!isOpen) return null;

    const submit = async (e) => {
        e.preventDefault();
        const id = loginId.trim().toUpperCase();
        if (!/^DF\d{5,}$/.test(id)) {
            setError(localize('Enter your DFunded Account ID, for example DF1234567.'));
            return;
        }
        if (password.length < 6) {
            setError(localize('Enter the trading password from your DFunded account email.'));
            return;
        }
        setBusy(true);
        setError('');
        try {
            const session = await DFundedCore.loginDFunded(id, password);
            // The rest of the app reads these keys to know a trader is signed in.
            localStorage.setItem('account_id', (session.account && session.account.login_id) || id);
            localStorage.setItem('account_type', 'real');
            window.location.reload();
        } catch (err) {
            setError((err && err.message) || localize('DFunded could not sign you in.'));
            setBusy(false);
        }
    };

    const field_style = {
        width       : '100%',
        height      : '40px',
        padding     : '0 12px',
        boxSizing   : 'border-box',
        border      : '1px solid var(--border-normal, #d6dadb)',
        borderRadius: '6px',
        fontSize    : '14px',
        background  : 'var(--general-main-1, #fff)',
        color       : 'inherit',
    };

    return (
        <div
            role='presentation'
            onMouseDown={() => !busy && onClose()}
            style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
        >
            <form
                role='dialog'
                aria-modal='true'
                aria-labelledby='dfunded-login-title'
                onMouseDown={e => e.stopPropagation()}
                onSubmit={submit}
                style={{ width: '100%', maxWidth: '420px', background: 'var(--general-main-1, #fff)', color: 'var(--text-prominent, #333)', borderRadius: '10px', padding: '20px', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <span style={{ display: 'inline-flex', width: '40px', height: '40px', alignItems: 'center', justifyContent: 'center', borderRadius: '10px', background: 'rgba(0,167,114,0.15)', color: '#00a772', fontWeight: 700 }}>DF</span>
                    <div>
                        <h2 id='dfunded-login-title' style={{ margin: 0, fontSize: '16px' }}>{localize('Log in with DFunded')}</h2>
                        <p style={{ margin: '4px 0 0', fontSize: '12px', opacity: 0.75 }}>
                            {localize('Use the Account ID and trading password from your DFunded account email. It is not your dfunded.site password.')}
                        </p>
                    </div>
                </div>
                <label htmlFor='dfunded-login-id' style={{ display: 'block', fontSize: '12px', fontWeight: 600, margin: '10px 0 6px' }}>{localize('Account ID')}</label>
                <input
                    id='dfunded-login-id'
                    type='text'
                    autoComplete='username'
                    autoCapitalize='characters'
                    spellCheck={false}
                    placeholder='DF1234567'
                    value={loginId}
                    disabled={busy}
                    onChange={e => { setLoginId(e.target.value); setError(''); }}
                    style={{ ...field_style, fontFamily: 'monospace' }}
                />
                <label htmlFor='dfunded-login-password' style={{ display: 'block', fontSize: '12px', fontWeight: 600, margin: '10px 0 6px' }}>{localize('Trading password')}</label>
                <input
                    id='dfunded-login-password'
                    type='password'
                    autoComplete='current-password'
                    value={password}
                    disabled={busy}
                    onChange={e => { setPassword(e.target.value); setError(''); }}
                    style={field_style}
                />
                {error && (
                    <p role='alert' style={{ margin: '10px 0 0', padding: '8px 10px', borderRadius: '6px', fontSize: '12px', background: 'rgba(236,63,121,0.12)', color: '#c93a5e' }}>{error}</p>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
                    <Button type='button' variant='secondary' size='md' label={localize('Cancel')} disabled={busy} onClick={onClose} />
                    <Button type='submit' variant='primary' size='md' label={busy ? localize('Signing in…') : localize('Log in')} disabled={busy || !loginId.trim() || !password} />
                </div>
            </form>
        </div>
    );
};

export default DFundedLoginModal;
